import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { IAMService, PersonaType, SortDirection, User, UserSortField } from '@vendasta/iam';
import type { AssociatedUser } from '@vendasta/protos/business_center/v1/types';
import {
  Observable,
  Subject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  forkJoin,
  map,
  of,
  shareReplay,
  startWith,
  switchMap,
  take,
  timer,
} from 'rxjs';
import { InboxService } from '../../../../core/src/lib/inbox.service';
import { conversationAssigneeUserId } from '../../../../core/src/lib/conversation-utils';
import { ConversationDetail } from '../../../../core/src/lib/interface/conversation.interface';
import { ConversationService } from '../../../../core/src/lib/state/conversation.service';
import { ACCOUNT_GROUP_ID_TOKEN, PARTNER_ID_TOKEN, USER_ID_TOKEN } from '../../../../core/src/lib/tokens';
import { InboxBusinessUsersService, userDisplayName } from '../../../../core/src/lib/state/inbox-business-users.service';

export type AssigneeState = 'unassigned' | 'assigned-to-me' | 'assigned-to-other';

export interface AssignableUser {
  userId: string;
  displayName: string;
  hiddenFromViewer?: boolean;
}

type BusinessUsersLoad =
  | { status: 'loading' }
  | { status: 'loaded'; users: AssignableUser[] }
  | { status: 'failed' };

const LOADING_BUSINESS_USERS: BusinessUsersLoad = { status: 'loading' };
const NO_BUSINESS_USERS: BusinessUsersLoad = { status: 'loaded', users: [] };

@Injectable()
export class ConversationAssignService {
  private readonly inboxService = inject(InboxService);
  private readonly iamService = inject(IAMService);
  private readonly conversationService = inject(ConversationService);
  private readonly businessUsersService = inject(InboxBusinessUsersService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly partnerId = toSignal(inject(PARTNER_ID_TOKEN));
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);
  private readonly currentUserId = toSignal(inject(USER_ID_TOKEN));
  private readonly destroyRef = inject(DestroyRef);

  private readonly searchTerm$$ = new Subject<string>();
  private readonly retryBusinessUsers$$ = new Subject<void>();
  private conversationDetail = signal<ConversationDetail | null>(null);

  readonly selectedUser = signal<AssignableUser | null>(null);
  readonly assigning = signal(false);
  readonly resolvingName = signal(false);

  readonly $assigneeState = computed<AssigneeState>(() => {
    const user = this.selectedUser();
    if (!user || user.hiddenFromViewer) return 'unassigned';
    const currentUserId = this.currentUserId();
    if (currentUserId && user.userId === currentUserId) return 'assigned-to-me';
    return 'assigned-to-other';
  });

  private readonly businessUsersLoad$: Observable<BusinessUsersLoad> = this.inboxService.isBusinessApp
    ? combineLatest([
        this.accountGroupId$.pipe(distinctUntilChanged()),
        this.retryBusinessUsers$$.pipe(startWith(undefined)),
      ]).pipe(
        switchMap(([accountGroupId]) =>
          accountGroupId ? this.fetchBusinessUsers(accountGroupId) : of(NO_BUSINESS_USERS),
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
      )
    : of(NO_BUSINESS_USERS);
  private readonly settledBusinessUsers$: Observable<AssignableUser[]> = this.businessUsersLoad$.pipe(
    filter((load) => load.status !== 'loading'),
    map((load) => (load.status === 'loaded' ? load.users : [])),
  );
  private readonly $businessUsersLoad = toSignal(this.businessUsersLoad$, { initialValue: LOADING_BUSINESS_USERS });
  private readonly $businessUsers = computed(() => {
    const load = this.$businessUsersLoad();
    return load.status === 'loaded' ? load.users : null;
  });

  readonly $loadingUsers = computed(() => this.$businessUsersLoad().status === 'loading');
  readonly $usersLoadFailed = computed(() => this.$businessUsersLoad().status === 'failed');

  // Assigning yourself to a business you aren't a user of leaves an assignee the business can't see.
  readonly $canAssignToMe = computed(() => {
    const currentUserId = this.currentUserId();
    if (!currentUserId) return false;
    if (!this.inboxService.isBusinessApp || this.$usersLoadFailed()) return true;
    return (this.$businessUsers() ?? []).some((u) => u.userId === currentUserId);
  });

  readonly users$: Observable<AssignableUser[]> = this.searchTerm$$.pipe(
    startWith(''),
    debounceTime(this.inboxService.isBusinessApp ? 0 : 300),
    map((v) => (typeof v === 'string' ? v : '')),
    distinctUntilChanged(),
    switchMap((term) => this.searchUsers(term)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  setSearchTerm(term: string): void {
    this.searchTerm$$.next(term);
  }

  retryFailedUsersLoad(): void {
    if (this.$usersLoadFailed()) this.retryBusinessUsers$$.next();
  }

  setConversationDetail(detail: ConversationDetail | null): void {
    this.conversationDetail.set(detail);
    this.syncAssigneeFromDetail(detail);
  }

  assignToMe(): void {
    const userId = this.currentUserId();
    if (!userId || !this.$canAssignToMe()) return;
    const businessUser = this.$businessUsers()?.find((u) => u.userId === userId);
    if (businessUser) {
      this.assignUser(businessUser);
      return;
    }
    this.users$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
      map((users) => users.find((u) => u.userId === userId)),
    ).subscribe((cached) => {
      this.assignUser(cached ?? { userId, displayName: '' });
    });
  }

  assignUser(user: AssignableUser | null): void {
    const conversationId = this.conversationDetail()?.conversation?.conversationId;
    if (!conversationId) {
      this.snackbarService.openErrorSnack('No active conversation');
      return;
    }

    this.assigning.set(true);

    forkJoin({
      rpc: this.conversationService.assignConversation$(conversationId, user ? [user.userId] : []),
      minDelay: timer(400),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.selectedUser.set(user);
          this.assigning.set(false);
        },
        error: (err) => {
          this.assigning.set(false);
          this.snackbarService.openErrorSnack(err);
        },
      });
  }

  private syncAssigneeFromDetail(detail: ConversationDetail | null): void {
    if (this.assigning()) return;
    const assigneeId = conversationAssigneeUserId(detail);
    if (assigneeId === this.selectedUser()?.userId) return;
    if (assigneeId) {
      this.selectedUser.set({ userId: assigneeId, displayName: '' });
      this.resolveAssigneeName(assigneeId);
    } else {
      this.selectedUser.set(null);
    }
  }

  private resolveAssigneeName(userId: string): void {
    this.resolvingName.set(true);
    const knownUsers$ = this.inboxService.isBusinessApp ? this.settledBusinessUsers$ : this.users$;
    knownUsers$.pipe(
      take(1),
      switchMap((users) => {
        const cached = users.find((u) => u.userId === userId);
        // Assignees are often partner/staff who aren't associated users of
        // the account group, so they're absent from the cached list. Fall
        // back to a direct IAM lookup only on a miss to keep the shared
        // fetch as the common path (see WOW-2605).
        return cached ? of(cached) : this.lookupUserById(userId);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((resolved) => {
      const current = this.selectedUser();
      if (current?.userId === userId) {
        this.selectedUser.set(resolved);
      }
      this.resolvingName.set(false);
    });
  }

  private lookupUserById(userId: string): Observable<AssignableUser> {
    const hiddenUser: AssignableUser = { userId, displayName: '', hiddenFromViewer: true };
    return this.iamService.getMultiUsers([{ userId }]).pipe(
      map((users) => {
        const user = users?.[0];
        return user ? this.toAssignable(user) : hiddenUser;
      }),
      catchError(() => of(hiddenUser)),
    );
  }

  private searchUsers(term: string): Observable<AssignableUser[]> {
    return this.inboxService.isBusinessApp
      ? this.searchBusinessUsers(term)
      : this.searchPartnerUsers(term);
  }

  private searchBusinessUsers(term: string): Observable<AssignableUser[]> {
    return this.settledBusinessUsers$.pipe(map((users) => this.filterByTerm(users, term)));
  }

  private fetchBusinessUsers(accountGroupId: string): Observable<BusinessUsersLoad> {
    return this.businessUsersService.users$(accountGroupId).pipe(
      map((users): BusinessUsersLoad => ({
        status: 'loaded',
        users: users.map((u) => this.toAssignableFromAssociated(u)),
      })),
      catchError((err) => {
        console.warn('[assign-picker] business users unavailable; picker will retry when reopened', {
          accountGroupId,
          err,
        });
        return of<BusinessUsersLoad>({ status: 'failed' });
      }),
      startWith(LOADING_BUSINESS_USERS),
    );
  }

  private searchPartnerUsers(term: string): Observable<AssignableUser[]> {
    const namespace = this.partnerId();
    if (!namespace) return of([]);

    return this.iamService
      .listUsers(
        namespace, undefined, '', 25,
        [PersonaType.partner],
        term ? [term] : [],
        [
          { direction: SortDirection.SORT_DIRECTION_ASCENDING, field: UserSortField.USER_SORT_FIELD_FIRST_NAME },
          { direction: SortDirection.SORT_DIRECTION_ASCENDING, field: UserSortField.USER_SORT_FIELD_LAST_NAME },
        ],
      )
      .pipe(
        map((resp) => (resp.users ?? []).map((u) => this.toAssignable(u))),
        catchError((err) => {
          console.warn('[assign-picker] user search unavailable; picker will show empty list', { namespace, err });
          return of([] as AssignableUser[]);
        }),
      );
  }

  private filterByTerm(users: AssignableUser[], term: string): AssignableUser[] {
    if (!term) return users;
    const needle = term.toLowerCase();
    return users.filter((u) => u.displayName.toLowerCase().includes(needle));
  }

  private toAssignable(user: User): AssignableUser {
    return {
      userId: user.userId,
      displayName: userDisplayName(user),
    };
  }

  private toAssignableFromAssociated(user: AssociatedUser): AssignableUser {
    return {
      userId: user.userId || '',
      displayName: userDisplayName(user),
    };
  }
}
